import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Edit, InfoOutlined, Save } from '@mui/icons-material';
import { Button, Grid, InputBase, Paper, Typography, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { makeStyles } from '@mui/styles';
import { useQueryClient } from 'react-query';
import { useSnackbar } from 'notistack';
import { AdminProgressBar } from '../../../components/AdminProgressBar';
import { postPatientAlert } from '../../../lib/api/postPatientAlert';
import { PatientTabsContext } from '../../../lib/contexts';
import useMetric from '../../../lib/hooks/useMetric';

const useStyles = makeStyles((theme) => ({
  patientChartCard: {
    ...theme.globalClasses.patientChartCard,
    ...theme.globalClasses.adminTableWrapper,
  },
  textarea: {
    ...theme.typography.body2,
  },
  inputFrame: {
    ...theme.globalClasses.inputFrame,
    border: `1px solid ${theme.palette.alert.infoColor}`,
    backgroundColor: theme.palette.alert.infoBackground,
  },
  infoIcon: {
    width: 22,
    height: 22,
    color: theme.palette.alert.infoColor,
  },
  editSaveIcon: {
    width: 16,
    height: 18,
    color: theme.palette.alert.infoColor,
  },
  editSaveText: {
    fontWeight: 700,
    fontSize: '13px',
    lineHeight: '22px',
    letterSpacing: '0.46px',
  },
  headerContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
  },
  titleSection: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    height: '28px',
  },
}));

const PatientAlertBox = () => {
  const classes = useStyles();
  const theme = useTheme();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const {
    state: { currentTab },
  } = useContext(PatientTabsContext);

  const { isLoading, data, refetch } = useMetric('alert', currentTab.id);

  const [isSaving, setIsSaving] = useState(false);
  const [content, setContent] = useState('');
  const [lastContent, setLastContent] = useState('');
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    if (data && data.metric) {
      setContent(data.metric?.value || '');
      setLastContent(data.metric?.value || '');
    } else {
      // Clear content when switching tabs or while loading
      setContent('');
      setLastContent('');
    }
    // Reset editing state when switching tabs
    setIsEditing(false);
  }, [data, currentTab.id]);

  // refetch data when the tab changes
  useEffect(() => {
    refetch();
  }, [currentTab.id, refetch]);

  const handleChange = useCallback((event) => {
    const content = event?.target?.value;
    setContent(content);
  }, []);

  const handleSave = () => {
    if (isSaving || content === lastContent) {
      setIsEditing(false);
      return;
    }
    const cleanContent = content.trim();
    // allow users to clear alert; determining if 3 is required
    if (cleanContent === '' || cleanContent.length > 3) {
      setIsSaving(true);
      postPatientAlert(currentTab.id, cleanContent)
        .then(async () => {
          const queryName = `metric_alert${currentTab.id}`;
          await queryClient.invalidateQueries(queryName);
          setIsSaving(false);
          setLastContent(cleanContent);
          setIsEditing(false);

          // Show success notification
          enqueueSnackbar('Alert successfully saved', {
            variant: 'success',
            autoHideDuration: 3000,
          });
        })
        .catch((error) => {
          console.error('Error saving alert:', error);
          setIsSaving(false);

          // Show error notification
          enqueueSnackbar('Failed to save alert', {
            variant: 'error',
            autoHideDuration: 4000,
          });
        });
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  // Determine text color based on content
  const hasContent = content && content.trim().length > 0;
  const textColor = hasContent ? theme.palette.alert.infoColor : 'rgba(0, 0, 0, 0.6)';

  return (
    <Grid
      item
      container
    >
      <Paper className={classes.patientChartCard}>
        <Grid
          item
          container
          className={classes.inputFrame}
        >
          <Grid
            container
            className={classes.headerContainer}
          >
            <Grid
              item
              xs
            >
              <div className={classes.titleSection}>
                <InfoOutlined className={classes.infoIcon} />
                <Typography variant={'alertTitle'}>Important Reminder</Typography>
              </div>
            </Grid>
            <Grid item>
              <Button
                variant="text"
                size="small"
                onClick={isEditing ? handleSave : handleEdit}
                className={`${classes.editSaveText} print-hide`}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  color: theme.palette.alert.infoColor,
                  '&.Mui-disabled': {
                    color: theme.palette.alert.infoColor,
                  },
                  '& .MuiSvgIcon-root': {
                    color: theme.palette.alert.infoColor,
                  },
                  '& .MuiSvgIcon-root.Mui-disabled': {
                    color: theme.palette.alert.infoColor,
                  },
                }}
                disabled={isLoading || isSaving}
              >
                {isEditing ? 'Save' : 'Edit'}
                {isEditing ? <Save className={classes.editSaveIcon} /> : <Edit className={classes.editSaveIcon} />}
              </Button>
            </Grid>
          </Grid>

          <Grid
            item
            xs={10.75}
            style={{ padding: 0, my: 0, marginLeft: '30px' }}
          >
            <InputBase
              id="patient-alert-input"
              value={content}
              onChange={handleChange}
              className={classes.textarea}
              multiline
              minRows={2}
              disabled={!isEditing}
              placeholder={'Helpful reminders about any triggers, barriers, family notices, etc.'}
              sx={{
                width: '95%',
                padding: 0,
                marginBottom: '8px',
                color: textColor,
                backgroundColor: theme.palette.alert.infoBackground,
                '& .MuiInputBase-input::placeholder': {
                  color: textColor,
                  opacity: 0.8,
                },
                '& .MuiInputBase-input.Mui-disabled': {
                  color: textColor,
                  WebkitTextFillColor: textColor,
                },
              }}
            />
            {(isLoading || isSaving) && <AdminProgressBar style={{ margin: '0px auto 12px' }} />}
          </Grid>
        </Grid>
      </Paper>
    </Grid>
  );
};

export default PatientAlertBox;
